import { Injectable, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { AuthApiService } from '../../api/auth-api.service';
import { MemberApiService } from '../../api/member-api.service';
import { CacheResult, StorageService } from '../storage/storage.service';
import { LoggerService } from '../logger/logger.service';
import { OperationResultService } from '../operation-result/operation-result.service';
import { PushNotificationService } from '../push-notification/push-notification.service';
import { ActionQueueService } from '../action-queue/action-queue.service';
import { Member } from '../../models/member.model';

/**
 * Service d'authentification et de gestion de session utilisateur.
 *
 * Ce service gere:
 * - La connexion et deconnexion des utilisateurs
 * - Le stockage et la recuperation des informations utilisateur
 * - La gestion des permissions camera pour le scan QR
 * - La mise a jour du mot de passe
 *
 * Utilise Angular Signals pour une reactivite automatique de l'etat utilisateur.
 */
@Injectable({
    providedIn: 'root',
})
export class AuthService {
    /** Signal contenant l'utilisateur connecte ou null */
    private readonly userSignal = signal<Member | null>(null);

    /** Signal en lecture seule pour les composants */
    readonly currentUser = this.userSignal.asReadonly();

    /** Promise resolue quand les roles/groupes sont prets (refresh termine) */
    private _rolesReadyResolve!: () => void;
    readonly rolesReady = new Promise<void>((resolve) => {
        this._rolesReadyResolve = resolve;
    });

    /** Timestamp du dernier refresh des roles/groupes */
    private lastRolesRefresh = 0;
    private readonly REFRESH_THROTTLE = 5 * 60 * 1000; // 5 minutes

    /** Cle de stockage pour la permission camera */
    private readonly cameraPermissionKey = 'cameraPermissionGranted';

    /** Stream camera en cache pour eviter les demandes multiples */
    private cachedCameraStream: MediaStream | null = null;

    /** Fonction de nettoyage des listeners du stream */
    private cachedStreamCleanup: (() => void) | null = null;

    private readonly router = inject(Router);
    private readonly storageService = inject(StorageService);
    private readonly authApi = inject(AuthApiService);
    private readonly memberApi = inject(MemberApiService);
    private readonly logger = inject(LoggerService);
    private readonly operationResult = inject(OperationResultService);
    private readonly pushNotificationService = inject(PushNotificationService);
    private readonly actionQueueService = inject(ActionQueueService);

    /**
     * Initialise le service en chargeant l'utilisateur depuis le localStorage.
     */
    constructor() {
        this.initializeUserFromStorage();

        document.addEventListener('visibilitychange', () => {
            if (document.visibilityState === 'visible') {
                this.onAppResumed();
            }
        });
    }

    /**
     * Authentifie un utilisateur avec ses identifiants.
     * Recupere les informations du membre et les stocke en session.
     *
     * @param email - Adresse email de l'utilisateur
     * @param password - Mot de passe
     * @throws Error si l'authentification echoue
     */
    async login(email: string, password: string): Promise<void> {
        const { user, session } = await this.authApi.login(email, password);

        if (!session) {
            throw new Error('Session non etablie apres la connexion.');
        }

        if (!user) {
            throw new Error(
                'Utilisateur manquant apres la connexion Supabase.',
            );
        }

        // Recuperation parallele des informations du membre
        const [userName, userGroups, userRoles, hasDefaultPassword] =
            await Promise.all([
                this.memberApi.getMemberName(user.id),
                this.memberApi.getMemberGroups(user.id),
                this.memberApi.getMemberRoles(user.id),
                this.memberApi.getMemberPasswordStatus(user.id),
            ]);

        // Validation des donnees du membre
        if (
            !Array.isArray(userName) ||
            userName.length === 0 ||
            !userName[0]?.first_name ||
            !userName[0]?.last_name
        ) {
            this.logger.error('Echec de la recuperation du nom du membre:', {
                userId: user.id,
                userName,
            });
            throw new Error(
                'Impossible de recuperer vos informations. Veuillez reessayer ou contacter le support.',
            );
        }

        // Construction de l'objet membre
        const userInfo: Member = {
            id: user.id,
            email: user.email,
            firstName: userName[0].first_name,
            lastName: userName[0].last_name,
            groups: userGroups,
            roles: userRoles,
            hasDefaultPassword: hasDefaultPassword,
        };

        // Sauvegarde en session
        this.storageService.setLocalStorage('user', userInfo);
        this.userSignal.set(userInfo);

        // Initialisation des notifications push (fire-and-forget)
        this.pushNotificationService
            .initializeForMember(userInfo.id)
            .catch((err) =>
                this.logger.warn('Echec init push notifications:', err),
            );

        await this.router.navigate(['/']);
    }

    /**
     * Deconnecte l'utilisateur courant.
     * Nettoie le localStorage et les caches, puis redirige vers la page de connexion.
     */
    logout(): void {
        this.actionQueueService.reset();
        this.authApi.logout();
        this.storageService.clearLocalStorage();
        this.clearProtectedCaches();
        this.userSignal.set(null);
        this.router.navigate(['/login']);
    }

    /**
     * Retourne l'utilisateur actuellement connecte.
     *
     * @returns Member ou null si non connecte
     * @deprecated Utiliser le signal `currentUser` pour la reactivite.
     */
    getCurrentUser(): Member | null {
        return this.userSignal();
    }

    /**
     * Charge l'utilisateur depuis le localStorage au demarrage.
     * Lance un rafraichissement asynchrone des roles et groupes.
     */
    private initializeUserFromStorage(): void {
        const memberInfo: CacheResult<Member> =
            this.storageService.getLocalStorageItem<Member>('user');

        if (memberInfo === null || memberInfo === false) {
            this.logger.debug('Aucun utilisateur en session.');
            this._rolesReadyResolve();
            return;
        }

        this.userSignal.set(memberInfo);
        this.refreshUserRolesAndGroups(memberInfo).finally(() => {
            this.lastRolesRefresh = Date.now();
            this._rolesReadyResolve();
        });

        // Initialisation des notifications push pour les utilisateurs deja connectes
        this.pushNotificationService
            .initializeForMember(memberInfo.id)
            .catch((err) =>
                this.logger.warn('Echec init push notifications:', err),
            );
    }

    /**
     * Rafraichit les roles et groupes de l'utilisateur depuis la base de donnees.
     * Fire-and-forget: l'app demarre avec les donnees en cache, puis se met a jour.
     */
    private async refreshUserRolesAndGroups(member: Member): Promise<void> {
        try {
            const [groups, roles] = await Promise.all([
                this.memberApi.getMemberGroups(member.id, 0),
                this.memberApi.getMemberRoles(member.id, 0),
            ]);

            const updatedMember: Member = {
                ...member,
                groups,
                roles,
            };

            this.storageService.setLocalStorage('user', updatedMember);
            this.userSignal.set(updatedMember);
        } catch (error) {
            this.logger.warn(
                'Echec du rafraichissement des roles et groupes:',
                error,
            );
        }
    }

    /**
     * Rafraichit les roles/groupes quand l'app revient au premier plan.
     * Throttle de 5 minutes pour eviter le spam reseau.
     */
    private onAppResumed(): void {
        const user = this.userSignal();
        if (!user) return;
        if (Date.now() - this.lastRolesRefresh < this.REFRESH_THROTTLE) return;
        this.lastRolesRefresh = Date.now();
        this.refreshUserRolesAndGroups(user);
    }

    /**
     * Supprime les caches du Service Worker lies a l'utilisateur.
     */
    private clearProtectedCaches(): void {
        if (typeof window === 'undefined' || !('caches' in window)) {
            return;
        }

        const cacheIdentifiers = [
            'supabase-members-list',
            'supabase-member-data',
            'supabase-member-stats',
            'supabase-excuses',
            'supabase-appointments',
        ];

        caches
            .keys()
            .then((cacheNames) =>
                Promise.all(
                    cacheNames
                        .filter((cacheName) =>
                            cacheIdentifiers.some((identifier) =>
                                cacheName.includes(identifier),
                            ),
                        )
                        .map((cacheName) => caches.delete(cacheName)),
                ),
            )
            .catch((error) =>
                this.logger.error(
                    'Erreur lors de la suppression du cache SW:',
                    error,
                ),
            );
    }

    /**
     * Met a jour le mot de passe de l'utilisateur connecte.
     *
     * @param newPassword - Nouveau mot de passe
     */
    async updatePassword(newPassword: string): Promise<void> {
        const memberInfo: CacheResult<Member> =
            this.storageService.getLocalStorageItem<Member>('user');

        if (memberInfo === null || memberInfo === false) {
            this.logger.warn(
                'Tentative de MAJ mot de passe sans utilisateur connecte.',
            );
            return;
        }

        const updateResult = await this.authApi.updatePassword(newPassword);
        this.operationResult.handleResult(updateResult);

        if (updateResult.status === 'error') {
            return;
        }

        const updateFieldStatus = await this.authApi.updateHasDefaultPassword(
            memberInfo.id,
        );

        if (!updateFieldStatus) {
            return;
        }

        // Mise a jour de la session locale
        memberInfo.hasDefaultPassword = false;
        this.storageService.setLocalStorage('user', memberInfo);
        this.userSignal.set(memberInfo);
    }

    // ========================================================================
    // GESTION DE LA CAMERA
    // ========================================================================

    /**
     * Demande la permission d'acces a la camera.
     * Utilise un cache pour eviter les demandes multiples.
     *
     * @param constraints - Contraintes media (video par defaut)
     * @returns Stream media de la camera
     * @throws Error si la permission est refusee
     */
    async requestCameraPermission(
        constraints: MediaStreamConstraints = { video: true },
    ): Promise<MediaStream> {
        const storedPermission =
            this.storageService.getLocalStorageItem<boolean>(
                this.cameraPermissionKey,
            );

        let hasPermission = storedPermission === true;

        // Verification du statut de permission via l'API Permissions
        if (
            hasPermission &&
            typeof navigator !== 'undefined' &&
            'permissions' in navigator &&
            navigator.permissions
        ) {
            try {
                const permissionStatus = await navigator.permissions.query({
                    name: 'camera' as PermissionName,
                });

                if (permissionStatus.state !== 'granted') {
                    hasPermission = false;
                    this.clearCameraPermissionFlag();
                }
            } catch (error) {
                this.logger.debug(
                    'Impossible de verifier le statut de la permission camera:',
                    error,
                );
                hasPermission = false;
            }
        }

        try {
            // Retourner le stream en cache s'il est actif
            if (hasPermission) {
                if (this.cachedCameraStream && this.cachedCameraStream.active) {
                    return this.cachedCameraStream;
                }

                const stream =
                    await navigator.mediaDevices.getUserMedia(constraints);
                this.setCachedCameraStream(stream);
                this.logger.debug('Permission camera accordee (cache).');
                return stream;
            }

            // Nouvelle demande de permission
            const stream =
                await navigator.mediaDevices.getUserMedia(constraints);
            this.setCachedCameraStream(stream);
            this.storageService.setLocalStorage(this.cameraPermissionKey, true);
            this.logger.debug('Permission camera accordee.');
            return stream;
        } catch (error) {
            this.clearCameraPermissionFlag();
            this.logger.warn('Permission camera refusee:', error);
            throw error;
        }
    }

    /**
     * Efface le flag de permission camera et arrete le stream.
     */
    private clearCameraPermissionFlag(): void {
        this.resetCachedCameraStream(true);

        if (typeof window !== 'undefined') {
            window.localStorage.removeItem(this.cameraPermissionKey);
        }
    }

    /**
     * Met en cache le stream camera et configure les listeners.
     */
    private setCachedCameraStream(stream: MediaStream | null): void {
        if (this.cachedCameraStream === stream) {
            return;
        }

        this.detachCachedStreamListeners();
        this.cachedCameraStream = stream;

        if (!stream) {
            return;
        }

        // Listener pour detecter quand le stream devient inactif
        const handleTrackEnded = () => {
            if (
                !stream.active ||
                stream
                    .getTracks()
                    .every((track) => track.readyState === 'ended')
            ) {
                if (this.cachedCameraStream === stream) {
                    this.resetCachedCameraStream(false);
                }
            }
        };

        stream.getTracks().forEach((track) => {
            track.addEventListener('ended', handleTrackEnded);
            track.addEventListener('inactive', handleTrackEnded);
        });

        this.cachedStreamCleanup = () => {
            stream.getTracks().forEach((track) => {
                track.removeEventListener('ended', handleTrackEnded);
                track.removeEventListener('inactive', handleTrackEnded);
            });
        };
    }

    /**
     * Reinitialise le cache du stream camera.
     */
    private resetCachedCameraStream(stopTracks: boolean): void {
        if (stopTracks && this.cachedCameraStream) {
            this.cachedCameraStream
                .getTracks()
                .forEach((track) => track.stop());
        }

        this.detachCachedStreamListeners();
        this.cachedCameraStream = null;
    }

    /**
     * Detache les listeners du stream en cache.
     */
    private detachCachedStreamListeners(): void {
        if (this.cachedStreamCleanup) {
            this.cachedStreamCleanup();
            this.cachedStreamCleanup = null;
        }
    }
}
